import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import PromoService from '../../services/promoService';
import { supabase } from '../../lib/supabase';
import FollowButton from '../../components/ui/FollowButton';
import { getTalentFollowerCount, getTalentRoleCount } from '../../services/talentService';

const TalentDetailPage = () => {
  const { talentId } = useParams();
  const navigate = useNavigate();
  
  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [followerCount, setFollowerCount] = useState(0);
  const [roleCount, setRoleCount] = useState(0);

  useEffect(() => {
    const loadTalentDetails = async () => {
      if (!talentId) return;

      setLoading(true);
      setError(null);

      try {
        // Load complete talent profile with bio, roles, and musicals
        const result = await PromoService?.getTalentDetailForPromo(talentId);
        
        if (!result?.success) {
          setError(result?.error || 'Failed to load talent details');
          return;
        }

        setTalent(result?.data);

        // Load follower and role counts
        const [followerResult, roleResult] = await Promise.all([
          getTalentFollowerCount(talentId),
          getTalentRoleCount(talentId)
        ]);

        if (!followerResult?.error) {
          setFollowerCount(followerResult?.data || 0);
        }
        if (!roleResult?.error) {
          setRoleCount(roleResult?.data || 0);
        }

      } catch (err) {
        console.error('Error loading talent details:', err);
        setError('Failed to load talent details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadTalentDetails();
  }, [talentId]);

  const handleFollowChange = async (isFollowing) => {
    // Update follower count when follow status changes
    setFollowerCount(prev => isFollowing ? prev + 1 : Math.max(0, prev - 1));
  };

  const handleBack = () => {
    navigate('/#talent-section');
  };

  // Get public URL for headshot from Supabase storage
  const getHeadshotUrl = (headshotPath) => {
    if (!headshotPath) return null;
    
    // If already a full URL, return as-is
    if (headshotPath?.startsWith('http')) {
      return headshotPath;
    }
    
    // Generate public URL from storage path
    const { data } = supabase?.storage?.from('profile-images')?.getPublicUrl(headshotPath);
    
    return data?.publicUrl;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading talent profile..." />
      </div>
    );
  }

  if (error || !talent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8 text-center">
            <Icon name="AlertTriangle" size={64} className="text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-white mb-2">
              Talent Not Found
            </h2>
            <p className="text-blue-100 mb-6">{error || 'This talent profile could not be found'}</p>
            <Button onClick={handleBack} className="bg-white text-indigo-900 hover:bg-gray-100 rounded-lg">
              Back to Talent Showcase
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{talent?.display_name || `${talent?.first_name} ${talent?.last_name}`} - RoleCall Mentor</title>
        <meta name="description" content={talent?.bio || `Professional musical theatre mentor ${talent?.first_name} ${talent?.last_name} teaching roles and techniques on RoleCall`} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        {/* Navigation Header */}
        <nav className="container mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AppImage 
              src="/assets/images/rolecall_logo_wide-1756786874531.png" 
              alt="RoleCall Logo - Musical Theatre Learning Platform"
              className="h-10 w-auto cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>
          <Button
            variant="outline"
            onClick={handleBack}
            className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-full px-6 py-2"
            iconName="ArrowLeft"
            iconPosition="left"
          >
            Back to Talent
          </Button>
        </nav>

        {/* Talent Profile Hero */}
        <section className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8 mb-8"
          >
            <div className="flex flex-col lg:flex-row gap-8 items-start">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                {talent?.headshot_url ? (
                  <img
                    src={getHeadshotUrl(talent?.headshot_url)}
                    alt={`${talent?.first_name} ${talent?.last_name} professional headshot`}
                    className="w-48 h-48 rounded-2xl object-cover border-4 border-white border-opacity-20 shadow-xl"
                    onError={(e) => {
                      // Fallback to gradient icon if image fails to load
                      e.target.style.display = 'none';
                      const fallback = e?.target?.nextElementSibling;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : (
                  <div className="w-48 h-48 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-xl">
                    <Icon name="User" size={80} className="text-white opacity-75" />
                  </div>
                )}
                {talent?.headshot_url && (
                  <div 
                    className="w-48 h-48 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl items-center justify-center shadow-xl"
                    style={{ display: 'none' }}
                  >
                    <Icon name="User" size={80} className="text-white opacity-75" />
                  </div>
                )}
              </div>

              {/* Profile Info */}
              <div className="flex-1">
                <h1 className="text-4xl font-heading font-bold text-white mb-2">
                  {talent?.display_name || `${talent?.first_name} ${talent?.last_name}`}
                </h1>
                
                <div className="flex flex-wrap items-center gap-4 mb-6 text-blue-200">
                  {followerCount > 0 && (
                    <div className="flex items-center space-x-2">
                      <Icon name="Heart" size={18} />
                      <span>{followerCount} Follower{followerCount !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {roleCount > 0 && (
                    <div className="flex items-center space-x-2">
                      <Icon name="Users" size={18} />
                      <span>{roleCount} Role{roleCount !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {talent?.songs?.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <Icon name="Music" size={18} />
                      <span>{talent?.songs?.length} Song{talent?.songs?.length !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {talent?.musicals?.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <Icon name="Drama" size={18} />
                      <span>{talent?.musicals?.length} Musical{talent?.musicals?.length !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>

                {/* Follow Button */}
                <div className="mb-6">
                  <FollowButton talentId={talentId} onFollowChange={handleFollowChange} />
                </div>

                {/* Social Links */}
                <div className="flex items-center space-x-4 mb-6">
                  {talent?.instagram && (
                    <a
                      href={talent?.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-200 hover:text-white transition-colors"
                      aria-label="Instagram"
                    >
                      <Icon name="Instagram" size={24} />
                    </a>
                  )}
                  {talent?.youtube && (
                    <a
                      href={talent?.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-200 hover:text-white transition-colors"
                      aria-label="YouTube"
                    >
                      <Icon name="Youtube" size={24} />
                    </a>
                  )}
                  {talent?.website && (
                    <a
                      href={talent?.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-200 hover:text-white transition-colors"
                      aria-label="Website"
                    >
                      <Icon name="Globe" size={24} />
                    </a>
                  )}
                  {talent?.tiktok && (
                    <a
                      href={talent?.tiktok}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-200 hover:text-white transition-colors"
                      aria-label="TikTok"
                    >
                      <Icon name="Music" size={24} />
                    </a>
                  )}
                </div>

                {/* Bio Section */}
                {talent?.bio && (
                  <div className="prose prose-invert max-w-none">
                    <p className="text-blue-100 text-lg leading-relaxed whitespace-pre-wrap">
                      {talent?.bio}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Associated Roles Section */}
          {talent?.roles?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8 mb-8"
            >
              <h2 className="text-3xl font-heading font-bold text-white mb-6">
                Teaching Roles
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {talent?.roles?.map((role) => (
                  <button
                    key={role?.id}
                    onClick={() => navigate(`/role-detail-page/${role?.id}`)}
                    className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl p-4 hover:bg-opacity-10 transition-all text-left w-full cursor-pointer"
                  >
                    {/* Musical Poster */}
                    {role?.musical?.poster_url && (
                      <div className="mb-4">
                        <img
                          src={role?.musical?.poster_url}
                          alt={`${role?.musical?.title} poster featuring ${role?.character_name}`}
                          className="w-full h-48 object-cover rounded-lg"
                        />
                      </div>
                    )}

                    {/* Role Info */}
                    <h3 className="text-xl font-heading font-semibold text-white mb-2">
                      {role?.character_name}
                    </h3>
                    
                    <p className="text-blue-200 text-sm mb-2">
                      from <span className="font-medium">{role?.musical?.title}</span>
                    </p>

                    {/* Role Details */}
                    <div className="space-y-1 text-sm text-blue-200">
                      {role?.voice_type && (
                        <div className="flex items-center space-x-2">
                          <Icon name="Music" size={14} />
                          <span>{role?.voice_type}</span>
                        </div>
                      )}
                      {role?.role_type && (
                        <div className="flex items-center space-x-2">
                          <Icon name="Users" size={14} />
                          <span className="capitalize">{role?.role_type}</span>
                        </div>
                      )}
                      {role?.vocal_range && (
                        <div className="flex items-center space-x-2">
                          <Icon name="Volume2" size={14} />
                          <span>{role?.vocal_range}</span>
                        </div>
                      )}
                    </div>

                    {/* Click indicator */}
                    <div className="mt-4 flex items-center justify-end text-blue-300 text-sm">
                      <span className="mr-1">View Details</span>
                      <Icon name="ArrowRight" size={16} />
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Associated Songs Section */}
          {talent?.songs?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8"
            >
              <h2 className="text-3xl font-heading font-bold text-white mb-6">
                Featured Songs
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {talent?.songs?.map((song) => (
                  <button
                    key={song?.id}
                    onClick={() => navigate(`/song-detail-page/${song?.id}`)}
                    className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl p-4 hover:bg-opacity-10 transition-all text-left w-full cursor-pointer"
                  >
                    {/* Musical Poster */}
                    {song?.musical?.poster_url && (
                      <div className="mb-4">
                        <img
                          src={song?.musical?.poster_url}
                          alt={`${song?.musical?.title} poster featuring ${song?.title}`}
                          className="w-full h-48 object-cover rounded-lg"
                        />
                      </div>
                    )}

                    {/* Song Info */}
                    <h3 className="text-xl font-heading font-semibold text-white mb-2">
                      {song?.title}
                    </h3>
                    
                    <p className="text-blue-200 text-sm mb-2">
                      from <span className="font-medium">{song?.musical?.title}</span>
                    </p>

                    {/* Click indicator */}
                    <div className="mt-4 flex items-center justify-end text-blue-300 text-sm">
                      <span className="mr-1">View Song</span>
                      <Icon name="ArrowRight" size={16} />
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 text-center"
          >
            <Button
              size="large"
              onClick={() => navigate('/auth/register')}
              className="bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-full px-12 py-4"
              iconName="Sparkles"
              iconPosition="left"
            >
              Start Learning with {talent?.first_name}
            </Button>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default TalentDetailPage;